/* ==========================================================================
   motion.js — the page-wide motion floor. Four things and nothing else:

   · StaggeredFadeIn on [data-reveal]. Nothing else moves globally, and NOTHING
     ABOVE THE FOLD may carry [data-reveal] — a hero entrance tween from a
     deferred script paints the hero, snaps it invisible and animates it back,
     which added ~0.7s to reported LCP because Chrome skips opacity:0 elements.
   · document.fonts.ready -> ScrollTrigger.refresh(), because a pinned beat
     measured before the webfont lands is measured against the wrong height.
   · the degradation log: every session stamps enhanced or base and, when base,
     the name of the check that failed.
   · the ?dev tuner shell.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = root.classList.contains('reduced-motion');
  var enhanced = root.classList.contains('enhanced');

  /* --- Degradation log ---------------------------------------------- */
  function logBoot() {
    var boot = window.AdventoriaBoot || { mode: 'base', reason: 'no-prepaint-script' };
    var line = 'Adventoria · ' + boot.mode;
    if (boot.mode !== 'enhanced') line += ' · failed check: ' + (boot.reason || 'unknown');
    line += ' · ' + window.innerWidth + '×' + window.innerHeight;
    if (window.console && console.info) console.info(line);
  }

  /* --- StaggeredFadeIn ----------------------------------------------------
     Trigger t − 85vh · 0.15s stagger · 30px rise over 0.7s · opacity over 0.9s
     · easeInOutQuad. Plays ONCE and destroys itself; never reverses. Never used
     above the fold. The CSS start state is opacity 0.01, not 0 — see main.css.
     ------------------------------------------------------------------ */
  function staggeredFadeIn() {
    var nodes = document.querySelectorAll('[data-reveal]');
    if (!nodes.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-revealed');
      return;
    }

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        obs.unobserve(el);

        /* Siblings sharing a [data-reveal-group] enter as one 0.15s stagger. */
        var group = el.getAttribute('data-reveal-group');
        var peers = group
          ? el.parentNode.querySelectorAll('[data-reveal-group="' + group + '"]')
          : [el];

        for (var j = 0; j < peers.length; j++) {
          (function (node, index) {
            window.setTimeout(function () {
              node.classList.add('is-revealed');
              node.style.willChange = 'transform, opacity';
              /* Release will-change after a beat — a permanent one is a
                 permanent compositor layer. */
              window.setTimeout(function () { node.style.willChange = ''; }, 1100);
            }, index * 150);
          })(peers[j], j);
        }
      });
    }, { rootMargin: '0px 0px -85% 0px', threshold: 0 });

    for (var k = 0; k < nodes.length; k++) io.observe(nodes[k]);
  }

  /* ------------------------------------------------------------------
     3. ScrollTrigger housekeeping. No trigger is created here — sections own
        their own. This only guarantees they measure against the real page.
     ------------------------------------------------------------------ */
  function refreshHooks() {
    if (!window.ScrollTrigger) return;
    var ST = window.ScrollTrigger;

    /* ScrollTrigger and nothing else. There is no MotionPathPlugin on this site
       and there is not going to be one: every driver measures the path the
       browser laid out with path.getPointAtLength(), which is why the plugin
       could be deleted from layout/theme.liquid without changing a pixel. */
    if (window.gsap) window.gsap.registerPlugin(ST);

    var refresh = function () { ST.refresh(); };
    window.addEventListener('load', refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  }

  /* ------------------------------------------------------------------
     4. ?dev — the tuner shell. Sections register their own sliders through
        Adventoria.dev.slider(); this file owns the panel and the CSS variable
        write-back, nothing else.
     ------------------------------------------------------------------ */
  var dev = (function () {
    var panel = null;
    var open = false;

    function ensure() {
      if (panel) return panel;
      panel = document.createElement('div');
      panel.id = 'adventoria-dev';
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-label', 'Tuner');
      panel.style.cssText = [
        'position:fixed', 'right:12px', 'bottom:12px', 'z-index:9999',
        'width:280px', 'max-height:70vh', 'overflow:auto',
        'padding:12px 14px', 'border:1px solid var(--paper-edge)',
        'background:var(--paper-lift)', 'color:var(--ink)',
        'font:12px/1.4 var(--font-ui)', 'border-radius:2px',
        'box-shadow:var(--e2)'
      ].join(';');
      panel.innerHTML =
        '<div style="letter-spacing:.14em;text-transform:uppercase;color:var(--ink-soft);' +
        'margin-bottom:8px">Tuner</div><div data-dev-rows></div>';
      document.body.appendChild(panel);
      open = true;
      return panel;
    }

    return {
      get enabled() { return open; },
      /* slider('--assembly-scroll', 60, 300, 150, 'vh') -> live-rewrites the
         variable on :root and remembers nothing. */
      slider: function (name, min, max, value, unit, onInput) {
        var rows = ensure().querySelector('[data-dev-rows]');
        var row = document.createElement('label');
        row.style.cssText = 'display:grid;gap:2px;margin-bottom:10px';
        var out = document.createElement('span');
        out.textContent = name + '  ' + value + (unit || '');
        out.style.color = 'var(--ink-soft)';
        var input = document.createElement('input');
        input.type = 'range';
        input.min = min; input.max = max; input.step = (max - min) / 200;
        input.value = value;
        input.style.width = '100%';
        input.addEventListener('input', function () {
          var v = parseFloat(input.value);
          out.textContent = name + '  ' + v.toFixed(2) + (unit || '');
          if (onInput) onInput(v);
          else document.documentElement.style.setProperty(name, v + (unit || ''));
        });
        row.appendChild(out);
        row.appendChild(input);
        rows.appendChild(row);
        return input;
      }
    };
  })();

  function init() {
    logBoot();
    staggeredFadeIn();
    refreshHooks();

    window.Adventoria = window.Adventoria || {};
    window.Adventoria.motion = {
      reduced: reduced,
      enhanced: enhanced,
      /* Sections add will-change on beat enter and release it in
         onLeave/onLeaveBack. The complete permitted vocabulary is exactly
         these four strings — anything else is a permanent layer by accident. */
      WILL_CHANGE: ['transform', 'opacity', 'transform,opacity', 'auto']
    };

    if (/[?&]dev\b/.test(window.location.search)) {
      window.Adventoria.dev = dev;
      dev.slider('--assembly-scroll', 60, 300, 150, 'vh');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
